// ============================================================================
// Módulo Página principal — editor profesional de la portada.
//
// Flujo: BD -> bloques/banners/categorías -> tarjetas de sección -> guardar
// (cada control persiste al cambiar) -> publicar (snapshot que ve el
// storefront vía publicar_tienda). La diferencia Guardar vs Publicar se
// comunica con el estado de publicación en el encabezado, igual que en
// "Personalizar tienda" (api/estado-publicacion.js + api/publicaciones.js).
//
// Cada tipo de bloque tiene una forma de configuración distinta
// (esquema_configuracion en tipo_bloque_catalogo), así que se renderiza un
// editor específico por `codigo` en vez de un formulario genérico. No se
// crean ni borran bloques: solo se activan, reordenan y configuran.
//
// Todo el trabajo funcional reutiliza la capa de API existente
// (bloques-pagina, banners, categorias, publicaciones, estado-publicacion).
// Este archivo solo reconstruye la PRESENTACIÓN para que la pantalla se
// sienta como un editor profesional: tarjetas de sección con resumen
// visual, estado de visibilidad, posición y acciones claras.
// ============================================================================

import { mostrarToast } from '@plataforma/ui';
import { escaparHtml } from '@plataforma/utils';

import {
  crearBanner,
  eliminarBanner,
  listarBanners,
  reemplazarImagenBanner,
  reordenarBanners,
} from '../api/banners.js';
import {
  actualizarBloquePagina,
  intercambiarOrdenBloques,
  listarBloquesPagina,
} from '../api/bloques-pagina.js';
import { listarCategorias } from '../api/categorias.js';
import { obtenerEstadoPublicacion } from '../api/estado-publicacion.js';
import { publicarTienda } from '../api/publicaciones.js';
import { obtenerEstadoSesion } from '../estado-sesion.js';
import { exigirSesionEmpresa } from '../guardas.js';

import { montarShell } from './shell.js';

// Descripción breve por tipo: qué controla cada sección y dónde se edita.
const METADATA_TIPO = {
  banner_principal: {
    etiqueta: 'Banner principal',
    descripcion: 'Imágenes de portada que reciben a tus clientes.',
    accion: 'Agregar y ordenar tus banners.',
  },
  categorias: {
    etiqueta: 'Categorías destacadas',
    descripcion: 'Atajos a tus categorías más vendidas.',
    accion: 'Marca cuáles mostrar y en qué sección aparecen.',
  },
  propuesta_valor: {
    etiqueta: 'Propuesta de valor',
    descripcion: 'Los puntos de confianza que te diferencian.',
    accion: 'Editor de puntos de confianza.',
  },
  productos_destacados: {
    etiqueta: 'Productos destacados',
    descripcion: 'Los productos que marcas como "Destacado".',
    accion: 'Se controla la cantidad y la visibilidad.',
  },
  testimonios: {
    etiqueta: 'Testimonios de clientes',
    descripcion: 'Opiniones de clientes en tu portada.',
    accion: 'Editor de testimonios.',
  },
  contactos: {
    etiqueta: 'Contacto',
    descripcion: 'Datos de contacto de tu negocio.',
    accion: 'Se edita en Mi empresa.',
  },
  footer: {
    etiqueta: 'Pie de página',
    descripcion: 'El cierre de la portada.',
    accion: 'Sin configuración adicional.',
  },
};

// Íconos SVG ligeros (inline, sin dependencias) por sección.
const ICONO_TIPO = {
  banner_principal:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="2"/><path d="M21 15l-5-5-5 5"/></svg>',
  categorias:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>',
  propuesta_valor:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><path d="M9 9h.01M15 9h.01"/></svg>',
  productos_destacados:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l2.7 5.5 6 .9-4.3 4.2 1 6-5.4-2.8-5.4 2.8 1-6L3.3 9.4l6-.9L12 3z"/></svg>',
  testimonios:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M8 9h8M8 13h5"/></svg>',
  footer:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 10h18"/></svg>',
};

const ICONO_SUBIR =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 15l-6-6-6 6"/></svg>';
const ICONO_BAJAR =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';
const ICONO_TRASLADAR =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="9" cy="6" r="1.6"/><circle cx="15" cy="6" r="1.6"/><circle cx="9" cy="12" r="1.6"/><circle cx="15" cy="12" r="1.6"/><circle cx="9" cy="18" r="1.6"/><circle cx="15" cy="18" r="1.6"/></svg>';
const ICONO_CHEVRON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>';

export async function vistaPaginaPrincipal(contenedorRaiz) {
  if (!exigirSesionEmpresa()) return;

  const contenido = montarShell(contenedorRaiz, '/pagina-principal');
  contenido.innerHTML = `
    <div class="pf-pp" data-conjunto>
      <header class="pf-page-header pf-pp__cabecera">
        <div>
          <h1 class="pf-page-header__titulo">Página principal</h1>
          <p class="pf-pp__subtitulo">
            Activa, ordena y configura las secciones de la portada de tu tienda.
          </p>
        </div>
        <div class="pf-page-header__acciones pf-pp__acciones">
          <span class="pf-pp__estado" id="pf-pp-publicacion"></span>
        </div>
      </header>

      <div class="pf-alerta pf-alerta--neutral pf-pp__intro">
        <span class="pf-alerta__icono">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 8v4M12 16h.01M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/></svg>
        </span>
        <div>
          <div class="pf-alerta__titulo">Así se construye tu portada</div>
          <p style="margin-top:2px;">Cada tarjeta es una sección de la tienda. Actívala, ordénala y configura su contenido. Cuando termines, pulsa "Publicar cambios" para llevarla a tu tienda pública.</p>
        </div>
      </div>

      <div id="pf-bloques-lista" class="pf-pp__lista"><p class="pf-texto-suave">Cargando…</p></div>
    </div>
  `;

  await cargarBloques(contenido);
}

async function cargarBloques(contenido) {
  const destino = contenido.querySelector('#pf-bloques-lista');
  const { data: bloques, error } = await listarBloquesPagina();

  if (error) {
    destino.innerHTML = `<div class="pf-alerta pf-alerta--peligro"><span class="pf-alerta__icono">⚠️</span><p>No se pudieron cargar los bloques de la página principal.</p></div>`;
    return;
  }

  destino.innerHTML = '';
  bloques.forEach((bloque, indice) => {
    destino.appendChild(
      crearTarjetaBloque(bloque, {
        total: bloques.length,
        posicion: indice + 1,
        esPrimero: indice === 0,
        esUltimo: indice === bloques.length - 1,
        anterior: bloques[indice - 1] ?? null,
        siguiente: bloques[indice + 1] ?? null,
        recargar: () => cargarBloques(contenido),
      }),
    );
  });

  // El estado de publicación (Guardar vs Publicar) se pinta una vez al
  // cargar; los controles individuales persisten solos al cambiar.
  pintarEstadoPublicacion(contenido);
}

function pintarEstadoPublicacion(contenido) {
  const nodo = contenido.querySelector('#pf-pp-publicacion');
  if (!nodo) return;

  nodo.innerHTML =
    '<span class="pf-badge pf-badge--neutral"><svg class="pf-pp__badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg> Consultando estado…</span>';

  obtenerEstadoPublicacion().then(({ publicada, pendiente, error }) => {
    if (error) {
      nodo.innerHTML = '';
      return;
    }

    if (publicada && !pendiente) {
      nodo.innerHTML =
        '<span class="pf-badge pf-badge--exito"><svg class="pf-pp__badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg> Publicado</span>';
      return;
    }

    nodo.innerHTML = `
      <span class="pf-badge pf-badge--alerta"><svg class="pf-pp__badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg> Hay cambios sin publicar</span>
      <button type="button" class="pf-boton pf-boton--primario pf-boton--chico" id="pf-pp-publicar">
        <svg class="pf-pp__badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
        Publicar cambios
      </button>`;

    nodo
      .querySelector('#pf-pp-publicar')
      .addEventListener('click', () => publicarCambios(contenido, nodo));
  });
}

async function publicarCambios(contenido, nodo) {
  const boton = nodo.querySelector('#pf-pp-publicar');
  if (!boton) return;

  const { claims } = obtenerEstadoSesion();
  const empresaId = claims.empresa_id;
  if (!empresaId) return;

  boton.disabled = true;
  boton.textContent = 'Publicando…';

  const { error } = await publicarTienda(empresaId);
  if (error) {
    mostrarToast(error.message ?? 'No se pudo publicar.', { tono: 'peligro' });
    boton.disabled = false;
    boton.textContent = 'Publicar cambios';
    return;
  }
  mostrarToast('✓ Tienda publicada correctamente.', { tono: 'exito' });
  pintarEstadoPublicacion(contenido);
}

function crearTarjetaBloque(
  bloque,
  { total, posicion, esPrimero, esUltimo, anterior, siguiente, recargar },
) {
  const codigo = bloque.tipo_bloque.codigo;
  const meta = METADATA_TIPO[codigo] ?? { etiqueta: bloque.tipo_bloque.nombre, descripcion: '' };
  const activo = bloque.estado === 'activo';
  const icono = ICONO_TIPO[codigo] ?? ICONO_CHEVRON;

  const tarjeta = document.createElement('section');
  tarjeta.className = `pf-pp__tarjeta${activo ? '' : ' pf-pp__tarjeta--inactiva'}`;
  tarjeta.dataset.bloqueId = bloque.id;
  tarjeta.dataset.estado = bloque.estado;

  // ---- Cabecera de la tarjeta -------------------------------------------
  tarjeta.innerHTML = `
    <div class="pf-pp__cabecera-tarjeta">
      <button class="pf-pp__plegar" type="button" data-accion="plegar" aria-expanded="true" aria-label="Colapsar sección ${escaparHtml(meta.etiqueta)}">
        <span class="pf-pp__chevron">${ICONO_CHEVRON}</span>
      </button>

      <div class="pf-pp__orden" data-orden aria-label="Reordenar sección ${escaparHtml(meta.etiqueta)}">
        <span class="pf-pp__arreo">${ICONO_TRASLADAR}</span>
      </div>

      <span class="pf-pp__icono">${icono}</span>

      <div class="pf-pp__titular">
        <span class="pf-pp__nombre">${escaparHtml(meta.etiqueta)}</span>
        <span class="pf-pp__posicion">Sección ${posicion} de ${total}</span>
      </div>

      <div class="pf-pp__cabecera-acciones">
        <label class="pf-interruptor pf-pp__visible">
          <input type="checkbox" data-accion="toggle-estado" ${activo ? 'checked' : ''} />
          <span class="pf-interruptor__carril"></span>
          <span class="pf-pp__visible-texto">${activo ? 'Visible' : 'Oculta'}</span>
        </label>
      </div>
    </div>

    <div class="pf-pp__cuerpo" data-cuerpo>
      <p class="pf-pp__descripcion">${escaparHtml(meta.descripcion)}</p>
      <div class="pf-pp__configuracion" data-config></div>
    </div>
  `;

  // ---- Plegar / desplegar -----------------------------------------------
  const botonPlegar = tarjeta.querySelector('[data-accion="plegar"]');
  const cuerpo = tarjeta.querySelector('.pf-pp__cuerpo');
  botonPlegar.addEventListener('click', () => {
    const colapsada = cuerpo.classList.toggle('pf-pp__cuerpo--colapsado');
    botonPlegar.setAttribute('aria-expanded', String(!colapsada));
    tarjeta.classList.toggle('pf-pp__tarjeta--colapsada', colapsada);
  });

  // ---- Orden: subir / bajar (integrados en la tarjeta, con feedback) ----
  const orden = tarjeta.querySelector('[data-orden]');
  orden.title = 'Subir o bajar esta sección';
  orden.innerHTML = `
    <button type="button" class="pf-pp__orden-boton" data-accion="subir" aria-label="Subir sección" title="Subir" ${esPrimero ? 'disabled' : ''}>${ICONO_SUBIR}</button>
    <button type="button" class="pf-pp__orden-boton" data-accion="bajar" aria-label="Bajar sección" title="Bajar" ${esUltimo ? 'disabled' : ''}>${ICONO_BAJAR}</button>
  `;

  async function mover(otro, boton) {
    boton.disabled = true;
    tarjeta.classList.add('pf-pp__tarjeta--moviendo');
    const { error } = await intercambiarOrdenBloques(
      { id: bloque.id, orden: bloque.orden },
      { id: otro.id, orden: otro.orden },
    );
    if (error) {
      mostrarToast('No se pudo reordenar la sección.', { tono: 'peligro' });
      boton.disabled = false;
      tarjeta.classList.remove('pf-pp__tarjeta--moviendo');
      return;
    }
    await recargar();
  }

  const botonSubir = orden.querySelector('[data-accion="subir"]');
  if (anterior) {
    botonSubir.addEventListener('click', () => mover(anterior, botonSubir));
  }
  const botonBajar = orden.querySelector('[data-accion="bajar"]');
  if (siguiente) {
    botonBajar.addEventListener('click', () => mover(siguiente, botonBajar));
  }

  // ---- Visibilidad --------------------------------------------------------
  const interruptor = tarjeta.querySelector('[data-accion="toggle-estado"]');
  const textoVisible = tarjeta.querySelector('.pf-pp__visible-texto');
  interruptor.addEventListener('change', async (evento) => {
    const nuevoEstado = evento.target.checked ? 'activo' : 'inactivo';
    const { error } = await actualizarBloquePagina(bloque.id, { estado: nuevoEstado });
    if (error) {
      mostrarToast('No se pudo actualizar la visibilidad.', { tono: 'peligro' });
      evento.target.checked = !evento.target.checked;
      return;
    }
    tarjeta.classList.toggle('pf-pp__tarjeta--inactiva', nuevoEstado !== 'activo');
    textoVisible.textContent = nuevoEstado === 'activo' ? 'Visible' : 'Oculta';
    tarjeta.dataset.estado = nuevoEstado;
    mostrarToast(nuevoEstado === 'activo' ? 'Sección visible en la tienda.' : 'Sección oculta.', {
      tono: 'exito',
    });
  });

  // ---- Editor según tipo ---------------------------------------------------
  const config = tarjeta.querySelector('[data-config]');
  montarEditorConfiguracion(config, bloque, recargar);

  return tarjeta;
}

function montarEditorConfiguracion(cuerpo, bloque, recargar) {
  switch (bloque.tipo_bloque.codigo) {
    case 'banner_principal':
      return montarEditorBanners(cuerpo, bloque, recargar);
    case 'categorias':
      return montarEditorCategorias(cuerpo, bloque);
    case 'propuesta_valor':
      return montarEditorPropuestaValor(cuerpo, bloque);
    case 'productos_destacados':
      return montarEditorCantidad(cuerpo, bloque);
    case 'testimonios':
      return montarEditorTestimonios(cuerpo, bloque);
    case 'footer':
      cuerpo.innerHTML = `
        <div class="pf-alerta pf-alerta--neutral">
          <span class="pf-alerta__icono">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 16v-4M12 8h.01M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/></svg>
          </span>
          <div>
            <div class="pf-alerta__titulo">Esta sección no requiere configuración</div>
            <p style="margin-top:2px;">El pie se muestra automáticamente al activarlo. No hay contenido adicional que configurar aquí.</p>
          </div>
        </div>`;
      return;
    case 'contactos':
      cuerpo.innerHTML = `
        <div class="pf-alerta pf-alerta--neutral">
          <span class="pf-alerta__icono">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4h16v16H4z"/><path d="M2 9l10 5L22 9"/></svg>
          </span>
          <div>
            <div class="pf-alerta__titulo">Los datos de contacto se editan en "Mi empresa".</div>
            <p style="margin-top:2px;">Teléfono, WhatsApp, dirección y redes se configuran ahí y aparecen aquí automáticamente.</p>
          </div>
        </div>`;
      return;
    default:
      cuerpo.innerHTML = `<p class="pf-texto-suave">Este tipo de sección no tiene configuración todavía.</p>`;
  }
}

// ============================================================================
// Propuesta de valor
// ============================================================================
async function montarEditorPropuestaValor(cuerpo, bloque) {
  const items = bloque.configuracion?.items ?? [];
  const valorInicial = items
    .map((item) => `${item.titulo ?? ''} | ${item.descripcion ?? ''}`)
    .join('\n');

  cuerpo.innerHTML = `
    ${resumenContenido(`${items.length} de 4 puntos configurados`)}
    <div class="pf-campo">
      <label for="pf-propuesta-${bloque.id}">Puntos de confianza</label>
      <textarea id="pf-propuesta-${bloque.id}" rows="4" placeholder="Atención cercana | Respondemos por WhatsApp&#10;Entrega coordinada | Te ayudamos con recojo o envío">${escapeTextarea(valorInicial)}</textarea>
      <small>Una línea por punto, usando "Título | Descripción". Máximo 4 puntos.</small>
    </div>
    <div class="pf-pp__pie-accion">
      <button type="button" class="pf-boton pf-boton--secundario pf-boton--chico" id="pf-propuesta-guardar-${bloque.id}">
        <svg class="pf-pp__ic-btn" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg>
        Guardar propuesta
      </button>
    </div>
  `;

  cuerpo.querySelector(`#pf-propuesta-guardar-${bloque.id}`).addEventListener('click', async () => {
    const textarea = cuerpo.querySelector(`#pf-propuesta-${bloque.id}`);
    const nuevosItems = textarea.value
      .split('\n')
      .map((linea) => {
        const [titulo, ...descripcion] = linea.split('|');
        return {
          titulo: titulo?.trim(),
          descripcion: descripcion.join('|').trim(),
        };
      })
      .filter((item) => item.titulo && item.descripcion)
      .slice(0, 4);

    const boton = cuerpo.querySelector(`#pf-propuesta-guardar-${bloque.id}`);
    boton.disabled = true;
    const { error } = await actualizarBloquePagina(bloque.id, {
      configuracion: { items: nuevosItems },
    });
    boton.disabled = false;
    mostrarToast(error ? 'No se pudo guardar.' : '✓ Propuesta guardada.', {
      tono: error ? 'peligro' : 'exito',
    });
  });
}

// ============================================================================
// Testimonios — editor visual profesional
// ============================================================================
const LIMITE_TESTIMONIOS = 8;

const ICONO_USUARIO =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
const ICONO_EDITAR =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
const ICONO_ELIMINAR =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>';

async function montarEditorTestimonios(cuerpo, bloque) {
  // Copia mutable de los testimonios para edición local
  let testimonios = [...(bloque.configuracion?.items ?? [])].map((t, i) => ({
    ...t,
    id: t.id ?? `temp-${Date.now()}-${i}`,
    visible: t.visible !== false, // por defecto visible
    orden: t.orden ?? i,
  }));

  let editandoId = null;

  // Función para renderizar la vista completa
  async function render() {
    const hayTestimonios = testimonios.length > 0;
    const contador = `${testimonios.length} de ${LIMITE_TESTIMONIOS} testimonios`;
    const enLimite = testimonios.length >= LIMITE_TESTIMONIOS;

    cuerpo.innerHTML = `
      <div class="pf-pp__testimonios-editor">
        ${resumenContenido(contador)}

        <!-- Toolbar -->
        <div class="pf-pp__testimonios-toolbar">
          <span class="pf-pp__testimonios-desc">Muestra opiniones reales para generar confianza en tu tienda.</span>
          <button
            type="button"
            class="pf-boton pf-boton--primario pf-boton--chico"
            id="pf-testimonio-agregar"
            ${enLimite ? 'disabled' : ''}
            aria-label="${enLimite ? 'Límite de 8 testimonios alcanzado' : 'Agregar testimonio'}"
          >
            <svg class="pf-pp__ic-btn" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
            ${enLimite ? 'Límite alcanzado (8)' : '+ Agregar testimonio'}
          </button>
        </div>

        <!-- Formulario inline para crear/editar -->
        <div class="pf-pp__testimonio-form" id="pf-testimonio-form-${bloque.id}" hidden>
          ${renderFormularioTestimonio(editandoId ? testimonios.find((t) => t.id === editandoId) : null, bloque.id)}
        </div>

        <!-- Lista de testimonios -->
        <div id="pf-testimonios-lista" class="pf-pp__testimonios-lista">
          ${
            hayTestimonios
              ? testimonios
                  .sort((a, b) => a.orden - b.orden)
                  .map((t, idx) => renderTarjetaTestimonio(t, idx, testimonios.length))
                  .join('')
              : `
              <div class="pf-vacio pf-vacio--compacto pf-pp__testimonio-vacio">
                <span class="pf-vacio__icono">${ICONO_USUARIO}</span>
                <div class="pf-vacio__titulo">Aún no tienes testimonios</div>
                <p class="pf-vacio__texto">Las opiniones de tus clientes ayudan a generar confianza y mejorar la conversión.</p>
                <div class="pf-vacio__acciones">
                  <button type="button" class="pf-boton pf-boton--primario pf-boton--chico" data-vacio-agregar ${enLimite ? 'disabled' : ''}>
                    <svg class="pf-pp__ic-btn" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
                    Agregar primer testimonio
                  </button>
                </div>
              </div>
            `
          }
        </div>
      </div>
    `;

    // Vincular eventos
    vincularEventos();
  }

  // Renderiza una tarjeta de testimonio
  function renderTarjetaTestimonio(t, idx, total) {
    const iniciales = obtenerIniciales(t.nombre);
    const colorAvatar = colorParaIniciales(t.nombre);
    const tieneCargo = t.cargo && t.cargo.trim();
    const cargoTexto = tieneCargo ? escaparHtml(t.cargo) : '';
    const opinionCorta =
      t.descripcion.length > 120 ? t.descripcion.slice(0, 120) + '…' : t.descripcion;

    return `
      <article class="pf-pp__testimonio-tarjeta${!t.visible ? ' pf-pp__testimonio-tarjeta--oculto' : ''}" data-testimonio-id="${t.id}">
        <div class="pf-pp__testimonio-cabecera">
          <span class="pf-pp__testimonio-avatar" style="background:${colorAvatar}">${iniciales}</span>
          <div class="pf-pp__testimonio-info-principal">
            <strong class="pf-pp__testimonio-nombre">${escaparHtml(t.nombre)}</strong>
            ${cargoTexto ? `<span class="pf-pp__testimonio-cargo">${cargoTexto}</span>` : ''}
          </div>
          <div class="pf-pp__testimonio-estado">
            <label class="pf-pp__testimonio-toggle" title="${t.visible ? 'Ocultar en tienda' : 'Mostrar en tienda'}">
              <input type="checkbox" class="pf-pp__testimonio-visibilidad" ${t.visible ? 'checked' : ''} data-accion="toggle-visibilidad" />
              <span class="pf-pp__testimonio-toggle-carril"></span>
              <span class="pf-pp__testimonio-toggle-texto">${t.visible ? 'Visible' : 'Oculto'}</span>
            </label>
          </div>
        </div>
        <blockquote class="pf-pp__testimonio-opinion">"${escaparHtml(opinionCorta)}"</blockquote>
        <footer class="pf-pp__testimonio-acciones">
          <div class="pf-pp__testimonio-orden" data-testimonio-orden>
            <button type="button" class="pf-pp__orden-boton" data-accion="subir" title="Mover arriba" ${idx === 0 ? 'disabled' : ''} aria-label="Mover arriba">${ICONO_SUBIR}</button>
            <button type="button" class="pf-pp__orden-boton" data-accion="bajar" title="Mover abajo" ${idx === total - 1 ? 'disabled' : ''} aria-label="Mover abajo">${ICONO_BAJAR}</button>
          </div>
          <div class="pf-pp__testimonio-acciones-secundarias">
            <button type="button" class="pf-pp__accion-secundaria" data-accion="editar" title="Editar" aria-label="Editar testimonio">${ICONO_EDITAR}</button>
            <button type="button" class="pf-pp__accion-secundaria pf-pp__accion-secundaria--peligro" data-accion="eliminar" title="Eliminar" aria-label="Eliminar testimonio">${ICONO_ELIMINAR}</button>
          </div>
        </footer>
      </article>
    `;
  }

  // Renderiza el formulario (crear o editar)
  function renderFormularioTestimonio(testimonioExistente, bloqueId) {
    const esEdicion = !!testimonioExistente;
    const t = testimonioExistente || { nombre: '', descripcion: '', cargo: '', visible: true };

    return `
      <div class="pf-pp__testimonio-form-header">
        <h4 class="pf-pp__testimonio-form-titulo">${esEdicion ? 'Editar testimonio' : 'Nuevo testimonio'}</h4>
        <button type="button" class="pf-pp__testimonio-form-cerrar" data-accion="cancelar" aria-label="Cancelar">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
      <div class="pf-pp__testimonio-form-campos">
        <div class="pf-campo">
          <label for="pf-test-nombre-${bloqueId}">Nombre del cliente <span class="pf-pp__requerido" aria-hidden="true">*</span></label>
          <input
            type="text"
            id="pf-test-nombre-${bloqueId}"
            value="${escaparHtml(t.nombre)}"
            placeholder="Ej. Juan Ramos"
            required
            maxlength="80"
            aria-required="true"
          />
        </div>
        <div class="pf-campo">
          <label for="pf-test-opinion-${bloqueId}">Opinión <span class="pf-pp__requerido" aria-hidden="true">*</span></label>
          <textarea
            id="pf-test-opinion-${bloqueId}"
            rows="4"
            placeholder="Escribe la opinión del cliente..."
            required
            maxlength="500"
            aria-required="true"
          >${escaparHtml(t.descripcion)}</textarea>
          <small class="pf-pp__contador-caracteres"><span id="pf-test-contador-${bloqueId}">${t.descripcion.length}</span> / 500 caracteres</small>
        </div>
        <div class="pf-campo">
          <label for="pf-test-cargo-${bloqueId}">Ciudad, cargo o información adicional (opcional)</label>
          <input
            type="text"
            id="pf-test-cargo-${bloqueId}"
            value="${escaparHtml(t.cargo || '')}"
            placeholder="Ej. Lima · Cliente frecuente · Gerente"
            maxlength="100"
          />
        </div>
        <div class="pf-campo pf-pp__campo-toggle">
          <label class="pf-interruptor pf-pp__form-visibilidad">
            <input type="checkbox" id="pf-test-visible-${bloqueId}" ${t.visible ? 'checked' : ''} />
            <span class="pf-interruptor__carril"></span>
            <span class="pf-interruptor__texto">Mostrar este testimonio en la tienda</span>
          </label>
        </div>
      </div>
      <div class="pf-pp__testimonio-form-acciones">
        <button type="button" class="pf-boton pf-boton--secundario pf-boton--chico" data-accion="cancelar">Cancelar</button>
        <button type="button" class="pf-boton pf-boton--primario pf-boton--chico" data-accion="guardar" ${esEdicion ? '' : ''}>
          <svg class="pf-pp__ic-btn" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg>
          ${esEdicion ? 'Guardar cambios' : 'Agregar testimonio'}
        </button>
      </div>
    `;
  }

  // Vincula todos los eventos de la vista
  function vincularEventos() {
    // Botón agregar / empty state
    const btnAgregar = cuerpo.querySelector('#pf-testimonio-agregar');
    const btnVacioAgregar = cuerpo.querySelector('[data-vacio-agregar]');
    const abrirFormulario = () => {
      editandoId = null;
      renderFormularioInline();
    };
    btnAgregar?.addEventListener('click', abrirFormulario);
    btnVacioAgregar?.addEventListener('click', abrirFormulario);

    // Formulario inline
    function renderFormularioInline() {
      const contenedor = cuerpo.querySelector(`#pf-testimonio-form-${bloque.id}`);
      if (!contenedor) return;
      const t = editandoId ? testimonios.find((x) => x.id === editandoId) : null;
      contenedor.innerHTML = renderFormularioTestimonio(t, bloque.id);
      contenedor.hidden = false;
      contenedor.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      vincularFormulario(contenedor);
    }

    function vincularFormulario(contenedor) {
      // Contador de caracteres
      const textarea = contenedor.querySelector(`#pf-test-opinion-${bloque.id}`);
      const contador = contenedor.querySelector(`#pf-test-contador-${bloque.id}`);
      if (textarea && contador) {
        textarea.addEventListener('input', () => {
          contador.textContent = textarea.value.length;
        });
      }

      // Cerrar formulario
      contenedor.querySelectorAll('[data-accion="cancelar"]').forEach((btn) => {
        btn.addEventListener('click', () => {
          contenedor.hidden = true;
          editandoId = null;
        });
      });

      // Guardar formulario
      contenedor.querySelector('[data-accion="guardar"]').addEventListener('click', async () => {
        const nombre = contenedor.querySelector(`#pf-test-nombre-${bloque.id}`).value.trim();
        const descripcion = contenedor.querySelector(`#pf-test-opinion-${bloque.id}`).value.trim();
        const cargo = contenedor.querySelector(`#pf-test-cargo-${bloque.id}`).value.trim();
        const visible = contenedor.querySelector(`#pf-test-visible-${bloque.id}`).checked;

        if (!nombre || !descripcion) {
          mostrarToast('Completa el nombre y la opinión.', { tono: 'alerta' });
          return;
        }

        if (editandoId) {
          // Editar existente
          const idx = testimonios.findIndex((x) => x.id === editandoId);
          if (idx !== -1) {
            testimonios[idx] = {
              ...testimonios[idx],
              nombre,
              descripcion,
              cargo: cargo || undefined,
              visible,
            };
          }
        } else {
          // Nuevo - verificar límite
          if (testimonios.length >= LIMITE_TESTIMONIOS) {
            mostrarToast(`No puedes agregar más de ${LIMITE_TESTIMONIOS} testimonios.`, {
              tono: 'alerta',
            });
            return;
          }
          const nuevo = {
            id: `temp-${Date.now()}`,
            nombre,
            descripcion,
            cargo: cargo || undefined,
            visible,
            orden: testimonios.length,
          };
          testimonios.push(nuevo);
        }

        await persistirCambios();
        editandoId = null;
        contenedor.hidden = true;
        await render();
      });
    }

    // Acciones en tarjetas
    cuerpo.querySelectorAll('.pf-pp__testimonio-tarjeta').forEach((tarjeta) => {
      const id = tarjeta.dataset.testimonioId;

      // Toggle visibilidad
      tarjeta
        .querySelector('[data-accion="toggle-visibilidad"]')
        .addEventListener('change', async (e) => {
          const t = testimonios.find((x) => x.id === id);
          if (t) {
            t.visible = e.target.checked;
            tarjeta.classList.toggle('pf-pp__testimonio-tarjeta--oculto', !t.visible);
            tarjeta.querySelector('.pf-pp__testimonio-toggle-texto').textContent = t.visible
              ? 'Visible'
              : 'Oculto';
            await persistirCambios();
          }
        });

      // Editar
      tarjeta.querySelector('[data-accion="editar"]').addEventListener('click', () => {
        editandoId = id;
        renderFormularioInline();
      });

      // Eliminar
      tarjeta.querySelector('[data-accion="eliminar"]').addEventListener('click', async () => {
        const t = testimonios.find((x) => x.id === id);
        if (!t) return;
        if (
          !window.confirm(
            `¿Eliminar el testimonio de "${t.nombre}"? Esta acción no se puede deshacer.`,
          )
        )
          return;

        testimonios = testimonios.filter((x) => x.id !== id);
        // Reordenar
        testimonios.forEach((x, i) => {
          x.orden = i;
        });
        await persistirCambios();
        await render();
      });

      // Reordenar subir/bajar
      tarjeta.querySelector('[data-accion="subir"]').addEventListener('click', async () => {
        const idx = testimonios.findIndex((x) => x.id === id);
        if (idx > 0) {
          [testimonios[idx], testimonios[idx - 1]] = [testimonios[idx - 1], testimonios[idx]];
          testimonios.forEach((x, i) => {
            x.orden = i;
          });
          await persistirCambios();
          await render();
        }
      });
      tarjeta.querySelector('[data-accion="bajar"]').addEventListener('click', async () => {
        const idx = testimonios.findIndex((x) => x.id === id);
        if (idx < testimonios.length - 1) {
          [testimonios[idx], testimonios[idx + 1]] = [testimonios[idx + 1], testimonios[idx]];
          testimonios.forEach((x, i) => {
            x.orden = i;
          });
          await persistirCambios();
          await render();
        }
      });
    });
  }

  // Persiste los cambios en la BD
  async function persistirCambios() {
    // Normalizar para BD: solo nombre, descripcion, cargo, visible
    const itemsParaBD = testimonios
      .sort((a, b) => a.orden - b.orden)
      .map((t) => ({
        nombre: t.nombre,
        descripcion: t.descripcion,
        ...(t.cargo ? { cargo: t.cargo } : {}),
        visible: t.visible,
      }));

    const { error } = await actualizarBloquePagina(bloque.id, {
      configuracion: { items: itemsParaBD },
    });
    if (error) {
      mostrarToast('No se pudo guardar el testimonio.', { tono: 'peligro' });
    } else {
      mostrarToast('✓ Testimonio guardado.', { tono: 'exito' });
    }
  }

  // Utilidades
  function obtenerIniciales(nombre) {
    const partes = nombre.trim().split(/\s+/);
    if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
    return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
  }

  function colorParaIniciales(nombre) {
    // Generar color consistente basado en el nombre
    let hash = 0;
    for (let i = 0; i < nombre.length; i++) hash = nombre.charCodeAt(i) + ((hash << 5) - hash);
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 65%, 45%)`;
  }

  // Render inicial
  await render();
}

function escapeTextarea(valor) {
  return String(valor ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// ============================================================================
// Productos destacados (cantidad + visibilidad; el producto se marca en
// Productos). Deja claro que aquí NO se crean productos.
// ============================================================================
async function montarEditorCantidad(cuerpo, bloque) {
  const cantidadActual = bloque.configuracion?.cantidad ?? 8;
  cuerpo.innerHTML = `
    <div class="pf-pp__cantidad">
      <div class="pf-campo pf-pp__cantidad-campo">
        <label for="pf-cantidad-${bloque.id}">Cantidad a mostrar</label>
        <div class="pf-pp__stepper">
          <button type="button" class="pf-pp__stepper-boton" data-paso="-1" aria-label="Reducir cantidad">−</button>
          <input id="pf-cantidad-${bloque.id}" type="number" min="1" max="24" value="${cantidadActual}" aria-label="Cantidad de productos a mostrar" />
          <button type="button" class="pf-pp__stepper-boton" data-paso="1" aria-label="Aumentar cantidad">＋</button>
        </div>
      </div>
      <div class="pf-pp__cantidad-nota">
        <span class="pf-pp__ic-note">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
        </span>
        <p>Estos productos vienen del módulo <strong>Productos</strong>: debes marcar cada uno como <strong>"Destacado"</strong> ahí. Aquí solo controlas cuántos y si se ven.</p>
      </div>
    </div>
  `;

  const input = cuerpo.querySelector(`#pf-cantidad-${bloque.id}`);
  async function guardarCantidad(valor) {
    const cantidad = Math.min(24, Math.max(1, Number(valor) || 8));
    input.value = cantidad;
    const { error } = await actualizarBloquePagina(bloque.id, { configuracion: { cantidad } });
    mostrarToast(error ? 'No se pudo guardar.' : '✓ Cantidad guardada.', {
      tono: error ? 'peligro' : 'exito',
    });
  }

  input.addEventListener('change', () => guardarCantidad(input.value));
  cuerpo.querySelectorAll('.pf-pp__stepper-boton').forEach((boton) => {
    boton.addEventListener('click', () => {
      const paso = Number(boton.dataset.paso);
      const nuevo = Math.min(24, Math.max(1, (Number(input.value) || 8) + paso));
      input.value = nuevo;
      guardarCantidad(nuevo);
    });
  });
}

// ============================================================================
// Categorías destacadas — mini-tarjetas seleccionables con resumen visual.
// ============================================================================
async function montarEditorCategorias(cuerpo, bloque) {
  cuerpo.innerHTML = `<p class="pf-texto-suave">Cargando categorías…</p>`;
  const { data: categorias, error } = await listarCategorias();
  if (error) {
    cuerpo.innerHTML = `<div class="pf-alerta pf-alerta--peligro"><span class="pf-alerta__icono">⚠️</span><p>No se pudieron cargar las categorías.</p></div>`;
    return;
  }

  const seleccionadas = new Set(bloque.configuracion?.ids_categoria ?? []);

  // Resumen superior: cuántas están seleccionadas.
  cuerpo.innerHTML = `
    ${resumenContenido(`${seleccionadas.size} de ${categorias.length} categorías en portada`)}
    <div class="pf-pp__categorias" data-categorias>
      ${
        categorias.length
          ? categorias
              .map((c) => {
                const activa = seleccionadas.has(c.id);
                const miniatura = c.imagen_url
                  ? `<span class="pf-pp__cat-img" style="background-image:url('${escaparHtml(c.imagen_url)}')" aria-hidden="true"></span>`
                  : `<span class="pf-pp__cat-img pf-pp__cat-img--sin" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="M21 15l-4-4-6 6"/></svg></span>`;
                return `
                  <label class="pf-pp__cat${activa ? ' pf-pp__cat--activa' : ''}" data-cat-id="${c.id}">
                    <input type="checkbox" class="pf-cat-destacada" value="${c.id}" ${activa ? 'checked' : ''} />
                    ${miniatura}
                    <span class="pf-pp__cat-marco">
                      <span class="pf-pp__cat-nombre">${escaparHtml(c.nombre)}</span>
                      <span class="pf-pp__cat-check">${ICONO_CHEVRON_CHECK}</span>
                    </span>
                  </label>`;
              })
              .join('')
          : `<div class="pf-vacio pf-vacio--compacto">
               <span class="pf-vacio__titulo">Sin categorías todavía</span>
               <p class="pf-vacio__texto">Crea categorías primero en el módulo Categorías.</p>
             </div>`
      }
    </div>
  `;

  cuerpo.querySelectorAll('.pf-cat-destacada').forEach((checkbox) => {
    const tarjeta = checkbox.closest('.pf-pp__cat');
    function reflejar(activa) {
      tarjeta?.classList.toggle('pf-pp__cat--activa', activa);
    }
    checkbox.addEventListener('change', async () => {
      const ids = Array.from(cuerpo.querySelectorAll('.pf-cat-destacada:checked')).map(
        (el) => el.value,
      );
      reflejar(ids.includes(checkbox.value));
      const { error: errorGuardado } = await actualizarBloquePagina(bloque.id, {
        configuracion: { ids_categoria: ids },
      });
      if (errorGuardado) {
        mostrarToast('No se pudo guardar.', { tono: 'peligro' });
      }
      const resumen = cuerpo.querySelector('.pf-pp__resumen-importante');
      if (resumen) {
        resumen.textContent = `${ids.length} de ${categorias.length} categorías en portada`;
      }
    });
  });
}

const ICONO_CHEVRON_CHECK =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>';

// ============================================================================
// Banner principal — lista de mini-tarjetas con miniatura, título, visibilidad
// y acciones; empty state profesional; drag & drop + flechas para el orden.
// ============================================================================
async function montarEditorBanners(cuerpo, bloque) {
  cuerpo.innerHTML = `<p class="pf-texto-suave">Cargando banners…</p>`;
  await pintarEditorBanners(cuerpo, bloque);
}

async function pintarEditorBanners(cuerpo, bloque) {
  const { data: bannersBrutos, error } = await listarBanners();
  if (error) {
    cuerpo.innerHTML = `<div class="pf-alerta pf-alerta--peligro"><span class="pf-alerta__icono">⚠️</span><p>No se pudieron cargar los banners.</p></div>`;
    return;
  }

  const bannersEnOrden = [...(bannersBrutos ?? [])].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
  const seleccionados = new Set(bloque.configuracion?.ids_banner ?? []);
  const incluidos = bannersEnOrden.filter((b) => seleccionados.has(b.id)).length;

  cuerpo.innerHTML = `
    <div class="pf-pp__banner-toolbar">
      <span class="pf-pp__info">${resumenContenido(`${incluidos} de ${bannersEnOrden.length} banners en la portada`)}</span>
      <button type="button" class="pf-boton pf-boton--primario pf-boton--chico" id="pf-banner-toggle-crear-${bloque.id}">
        <svg class="pf-pp__ic-btn" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
        Agregar banner
      </button>
    </div>

    <div class="pf-pp__banner-crear" id="pf-banner-crear-${bloque.id}" hidden>
      <div class="pf-pp__banner-crear-titulo">Nuevo banner</div>
      <div class="pf-pp__banner-crear-grid">
        <div class="pf-campo">
          <label for="pf-banner-titulo-${bloque.id}">Título del banner</label>
          <input type="text" id="pf-banner-titulo-${bloque.id}" placeholder="Ej. Colección de invierno" />
        </div>
        <div class="pf-campo">
          <label for="pf-banner-imagen-${bloque.id}">Imagen (1920px recomendado)</label>
          <input type="file" id="pf-banner-imagen-${bloque.id}" accept="image/jpeg,image/png,image/webp" />
        </div>
        <div class="pf-pp__banner-crear-accion">
          <button type="button" class="pf-boton pf-boton--primario pf-boton--chico" id="pf-banner-agregar-${bloque.id}">Agregar</button>
        </div>
      </div>
      <p class="pf-campo__error" id="pf-banner-crear-error-${bloque.id}" hidden></p>
    </div>

    <div id="pf-banners-lista" class="pf-pp__banners"></div>
  `;

  const lista = cuerpo.querySelector('#pf-banners-lista');

  async function renderLista() {
    if (bannersEnOrden.length === 0) {
      lista.innerHTML = `
        <div class="pf-vacio pf-vacio--compacto pf-pp__banner-vacio">
          <span class="pf-vacio__icono">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="2"/><path d="M21 15l-5-5-5 5"/></svg>
          </span>
          <div class="pf-vacio__titulo">Aún no hay banners</div>
          <p class="pf-vacio__texto">Crea el primero para darle una imagen de bienvenida a tu portada.</p>
          <div class="pf-vacio__acciones">
            <button type="button" class="pf-boton pf-boton--primario pf-boton--chico" data-vacio-crear>Agregar banner</button>
          </div>
        </div>`;
      lista.querySelector('[data-vacio-crear]')?.addEventListener('click', () => {
        contenedorCrear.hidden = false;
        contenedorCrear.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
      return;
    }

    lista.innerHTML = bannersEnOrden
      .map(
        (b, indice) => `
        <div
          class="pf-pp__banner-tarjeta"
          draggable="true"
          data-banner-id="${b.id}"
        >
          <span class="pf-pp__banner-arrastre" title="Arrastrar para reordenar">${ICONO_TRASLADAR}</span>
          <span class="pf-pp__banner-img">
            <img src="${escaparHtml(b.imagen_url)}" alt="${escaparHtml(b.titulo)}" />
          </span>
          <div class="pf-pp__banner-info">
            <strong class="pf-pp__banner-titulo">${escaparHtml(b.titulo)}</strong>
            <label class="pf-pp__banner-incluir">
              <input type="checkbox" class="pf-banner-incluido" value="${b.id}" ${seleccionados.has(b.id) ? 'checked' : ''} />
              <span class="pf-pp__banner-incluir-texto">${seleccionados.has(b.id) ? 'Mostrando en la portada' : 'Mostrar en la portada'}</span>
            </label>
          </div>
          <div class="pf-pp__banner-acciones">
            <div class="pf-pp__orden pf-pp__orden--chico" data-banner-orden>
              <button type="button" class="pf-pp__orden-boton" data-banner-subir="${b.id}" ${indice === 0 ? 'disabled' : ''} title="Subir" aria-label="Subir banner">${ICONO_SUBIR}</button>
              <button type="button" class="pf-pp__orden-boton" data-banner-bajar="${b.id}" ${indice === bannersEnOrden.length - 1 ? 'disabled' : ''} title="Bajar" aria-label="Bajar banner">${ICONO_BAJAR}</button>
            </div>
            <label class="pf-boton pf-boton--secundario pf-boton--chico pf-pp__banner-reemplazar-label">
              <svg class="pf-pp__ic-btn" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><path d="M3 21v-5h5"/></svg>
              Reemplazar
              <input type="file" class="pf-banner-reemplazar" data-banner-id="${b.id}" accept="image/jpeg,image/png,image/webp" style="display:none;" />
            </label>
            <button type="button" class="pf-boton pf-boton--peligro pf-boton--chico" data-borrar-banner="${b.id}">
              <svg class="pf-pp__ic-btn" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>
              Eliminar
            </button>
          </div>
        </div>`,
      )
      .join('');
  }

  await renderLista();

  // ---- Crear banner ----
  const contenedorCrear = cuerpo.querySelector(`#pf-banner-crear-${bloque.id}`);
  cuerpo.querySelector(`#pf-banner-toggle-crear-${bloque.id}`).addEventListener('click', () => {
    contenedorCrear.hidden = !contenedorCrear.hidden;
  });

  const botonAgregar = cuerpo.querySelector(`#pf-banner-agregar-${bloque.id}`);
  botonAgregar.addEventListener('click', async () => {
    const errorCrearEl = cuerpo.querySelector(`#pf-banner-crear-error-${bloque.id}`);
    const tituloInput = cuerpo.querySelector(`#pf-banner-titulo-${bloque.id}`);
    const imagenInput = cuerpo.querySelector(`#pf-banner-imagen-${bloque.id}`);
    const archivo = imagenInput.files?.[0];

    errorCrearEl.hidden = true;
    if (!tituloInput.value.trim() || !archivo) {
      errorCrearEl.textContent = 'Completa el título y elige una imagen.';
      errorCrearEl.hidden = false;
      return;
    }

    botonAgregar.disabled = true;
    botonAgregar.textContent = 'Subiendo…';
    const { error: errorCreacion } = await crearBanner(archivo, {
      titulo: tituloInput.value.trim(),
    });
    botonAgregar.disabled = false;
    botonAgregar.textContent = 'Agregar';

    if (errorCreacion) {
      errorCrearEl.textContent = errorCreacion.message ?? 'No se pudo crear el banner.';
      errorCrearEl.hidden = false;
      return;
    }

    tituloInput.value = '';
    imagenInput.value = '';
    mostrarToast('Banner creado.', { tono: 'exito' });
    const { data: renovados } = await listarBanners();
    bannersEnOrden.length = 0;
    bannersEnOrden.push(...(renovados ?? []).sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)));
    await renderLista();
    vincularLista();
  });

  // ---- Acciones de la lista ----
  function vincularLista() {
    lista.querySelectorAll('.pf-banner-incluido').forEach((checkbox) => {
      const labelTexto = checkbox
        .closest('.pf-pp__banner-incluir')
        ?.querySelector('.pf-pp__banner-incluir-texto');
      checkbox.addEventListener('change', async () => {
        const ids = Array.from(lista.querySelectorAll('.pf-banner-incluido:checked')).map(
          (el) => el.value,
        );
        seleccionados.clear();
        ids.forEach((id) => seleccionados.add(id));
        if (labelTexto) {
          labelTexto.textContent = checkbox.checked
            ? 'Mostrando en la portada'
            : 'Mostrar en la portada';
        }
        const { error: errorGuardado } = await actualizarBloquePagina(bloque.id, {
          configuracion: { ids_banner: ids },
        });
        mostrarToast(errorGuardado ? 'No se pudo guardar.' : '✓ Guardado.', {
          tono: errorGuardado ? 'peligro' : 'exito',
        });
      });
    });

    lista.querySelectorAll('[data-borrar-banner]').forEach((boton) => {
      boton.addEventListener('click', async () => {
        if (!window.confirm('¿Eliminar este banner?')) return;
        boton.disabled = true;
        const { error: errorBorrado } = await eliminarBanner(boton.dataset.borrarBanner);
        if (errorBorrado) {
          mostrarToast('No se pudo eliminar el banner.', { tono: 'peligro' });
          boton.disabled = false;
          return;
        }
        const id = boton.dataset.borrarBanner;
        const indice = bannersEnOrden.findIndex((b) => b.id === id);
        if (indice !== -1) bannersEnOrden.splice(indice, 1);
        seleccionados.delete(id);
        mostrarToast('Banner eliminado.', { tono: 'exito' });
        await renderLista();
      });
    });

    lista.querySelectorAll('.pf-banner-reemplazar').forEach((input) => {
      input.addEventListener('change', async () => {
        const archivo = input.files?.[0];
        if (!archivo) return;
        const tarjeta = input.closest('.pf-pp__banner-tarjeta');
        tarjeta.style.opacity = '0.55';
        const { error: errorReemplazo } = await reemplazarImagenBanner(
          input.dataset.bannerId,
          archivo,
        );
        tarjeta.style.opacity = '';
        if (errorReemplazo) {
          mostrarToast(errorReemplazo.message ?? 'No se pudo reemplazar la imagen.', {
            tono: 'peligro',
          });
          return;
        }
        mostrarToast('Imagen reemplazada.', { tono: 'exito' });
        const { data: renovados } = await listarBanners();
        bannersEnOrden.length = 0;
        bannersEnOrden.push(...(renovados ?? []).sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)));
        await renderLista();
      });
    });

    lista.querySelectorAll('[data-banner-subir], [data-banner-bajar]').forEach((boton) => {
      boton.addEventListener('click', async () => {
        const id = boton.dataset.bannerSubir ?? boton.dataset.bannerBajar;
        const direccion = boton.dataset.bannerSubir ? -1 : 1;
        const indice = bannersEnOrden.findIndex((b) => b.id === id);
        const nuevoIndice = indice + direccion;
        if (indice === -1 || nuevoIndice < 0 || nuevoIndice >= bannersEnOrden.length) return;
        [bannersEnOrden[indice], bannersEnOrden[nuevoIndice]] = [
          bannersEnOrden[nuevoIndice],
          bannersEnOrden[indice],
        ];
        await guardarOrden();
      });
    });

    // Drag & drop HTML5 para reordenar tarjetas (además de las flechas).
    let arrastrado = null;
    lista.querySelectorAll('.pf-pp__banner-tarjeta').forEach((tarjeta) => {
      tarjeta.addEventListener('dragstart', () => {
        arrastrado = tarjeta;
        tarjeta.style.opacity = '0.5';
      });
      tarjeta.addEventListener('dragend', () => {
        tarjeta.style.opacity = '';
        arrastrado = null;
      });
      tarjeta.addEventListener('dragover', (evento) => {
        evento.preventDefault();
        if (!arrastrado || arrastrado === tarjeta) return;
        tarjeta.classList.add('pf-pp__banner-tarjeta--sobre');
      });
      tarjeta.addEventListener('dragleave', () => {
        tarjeta.classList.remove('pf-pp__banner-tarjeta--sobre');
      });
      tarjeta.addEventListener('drop', async (evento) => {
        evento.preventDefault();
        tarjeta.classList.remove('pf-pp__banner-tarjeta--sobre');
        if (!arrastrado || arrastrado === tarjeta) return;
        const idsActuales = Array.from(lista.querySelectorAll('.pf-pp__banner-tarjeta')).map(
          (el) => el.dataset.bannerId,
        );
        const desde = idsActuales.indexOf(arrastrado.dataset.bannerId);
        const hasta = idsActuales.indexOf(tarjeta.dataset.bannerId);
        const [mover] = bannersEnOrden.splice(desde, 1);
        bannersEnOrden.splice(hasta, 0, mover);
        await guardarOrden();
      });
    });
  }

  async function guardarOrden() {
    await renderLista();
    vincularLista();
    const ids = bannersEnOrden.map((b) => b.id);
    const { error: errorOrden } = await reordenarBanners(ids);
    if (errorOrden) {
      mostrarToast('No se pudo guardar el orden.', { tono: 'peligro' });
      return;
    }
    mostrarToast('Orden guardado.', { tono: 'exito' });
  }

  await vincularLista();
}

// Helper visual: línea de resumen de contenido dentro de una tarjeta.
function resumenContenido(texto) {
  return `
    <div class="pf-pp__resumen" data-resumen>
      <span class="pf-pp__resumen-importante">${texto}</span>
    </div>`;
}
